// Settings: audio levels, feedback options (shake, flashing, damage numbers,
// FPS), gamepad deadzone and progress reset. Changes apply live through
// save.updateSettings. Launched with { returnTo } (default Menu); Back stops
// this scene and resumes / wakes the caller.

import Phaser from 'phaser';
import { audio } from '../audio';
import { SCENES } from '../config/constants';
import { COLORS } from '../config/palette';
import { save } from '../core/save';
import type { Settings } from '../core/types';
import { gridBackdrop } from '../ui/backdrops';
import {
  MenuNav,
  Panel,
  SliderRow,
  TYPE,
  ToggleRow,
  UI,
  backdrop,
  confirmDialog,
  createButton,
  footerHints,
  setMenuCursor,
  sfx,
  staggerIn,
  txt,
  type NavItem,
} from '../ui/kit';

export interface SettingsSceneData {
  returnTo?: string;
}

const ROW_W = 900;
const ROW_H = 58;

export class SettingsScene extends Phaser.Scene {
  private nav!: MenuNav;
  private returnTo: string = SCENES.MENU;
  private help!: Phaser.GameObjects.Text;
  private helpFor = new Map<NavItem, string>();
  private closing = false;

  constructor() {
    super(SCENES.SETTINGS);
  }

  create(data: SettingsSceneData = {}): void {
    this.returnTo = data?.returnTo ?? SCENES.MENU;
    this.closing = false;
    this.helpFor.clear();
    this.scene.bringToTop();
    setMenuCursor(this, true);

    // Over a running/paused caller: dim it. Standalone: draw our own background.
    const mgr = this.scene;
    const callerVisible = mgr.isPaused(this.returnTo) || mgr.isActive(this.returnTo);
    if (callerVisible) backdrop(this, 0.8);
    else gridBackdrop(this, COLORS.volt);

    const accent = COLORS.volt;
    const pw = 1020;
    const ph = 900;
    const px = (UI.W - pw) / 2;
    const py = (UI.H - ph) / 2 - 16;
    const panel = new Panel(this, px, py, pw, ph, { fill: COLORS.uiBg, fillAlpha: 0.94, accent, ticks: true, chamfer: 22, header: 96, headerAlpha: 0.1, scanlines: true });
    const title = txt(this, px + 52, py + 22, 'SETTINGS', { font: 'display', size: TYPE.h1, weight: 700, spacing: 8 });
    const sub = txt(this, px + pw - 52, py + 40, 'AUDIO  ·  FEEDBACK  ·  CONTROLS', { font: 'display', size: TYPE.label, color: COLORS.uiDim, spacing: 3, originX: 1 });

    this.nav = new MenuNav(this, {
      layout: 'vertical',
      onBack: () => this.close(),
      onFocus: (it) => this.help?.setText(it ? (this.helpFor.get(it) ?? '') : ''),
    });

    const cx = UI.W / 2;
    let y = py + 130;
    const rows: Phaser.GameObjects.Container[] = [];
    const s = save.settings;
    const apply = (patch: Partial<Settings>): void => {
      save.updateSettings(patch);
      try {
        audio.applySettings(save.settings);
      } catch {
        // audio optional
      }
    };
    const header = (label: string): void => {
      const t = txt(this, px + 64, y, label, { font: 'display', size: TYPE.label, color: accent, spacing: 4, weight: 700 });
      const g = this.add.graphics();
      g.lineStyle(1, COLORS.uiBorder, 1);
      g.lineBetween(px + 76 + t.width, y + 12, px + pw - 64, y + 12);
      rows.push(this.add.container(0, 0, [t, g]));
      y += 34;
    };
    const add = <T extends NavItem & Phaser.GameObjects.Container>(row: T, help: string): T => {
      this.nav.add(row);
      this.nav.bindPointer(row, row);
      this.helpFor.set(row, help);
      rows.push(row);
      y += ROW_H + 6;
      return row;
    };
    const slider = (label: string, key: keyof Settings, help: string, opts: { max?: number; step?: number } = {}): SliderRow =>
      add(
        new SliderRow(this, cx, y + ROW_H / 2, ROW_W, ROW_H, label, {
          value: Number(s[key]),
          max: opts.max ?? 1,
          step: opts.step ?? 0.05,
          accent,
          onChange: (v) => apply({ [key]: v } as Partial<Settings>),
        }),
        help,
      );
    const toggle = (label: string, key: keyof Settings, help: string): ToggleRow =>
      add(new ToggleRow(this, cx, y + ROW_H / 2, ROW_W, ROW_H, label, Boolean(s[key]), (v) => apply({ [key]: v } as Partial<Settings>), accent), help);

    header('AUDIO');
    const first = slider('Master volume', 'masterVolume', 'Overall loudness of the game.');
    slider('Music volume', 'musicVolume', 'Soundtrack level. Combat layers follow the action.');
    slider('Effects volume', 'sfxVolume', 'Weapons, impacts, abilities and interface sounds.');
    y += 6;
    header('FEEDBACK');
    slider('Screen shake', 'screenShake', 'Camera shake from impacts and explosions. 0% disables it.');
    toggle('Reduced flashing', 'reducedFlashing', 'Replaces full-screen flashes with soft tints and calms strobing effects.');
    toggle('Damage numbers', 'damageNumbers', 'Floating numbers when you hit enemies.');
    toggle('Show FPS', 'showFps', 'Frame-rate counter in the top-right corner of the HUD.');
    y += 6;
    header('CONTROLS');
    slider('Gamepad deadzone', 'deadzone', 'Stick movement ignored around the centre. Raise it if your mech drifts.', { max: 0.5, step: 0.05 });
    y += 14;

    // Data + back buttons.
    const by = py + ph - 72;
    const reset = createButton(this, px + 52 + 190, by, { label: 'Reset progress', width: 380, height: 60, variant: 'danger', sound: 'ui_click', onClick: () => this.confirmReset() }, this.nav);
    this.helpFor.set(reset, 'Wipe cores, unlocks, cosmetics and records. Settings are kept.');
    const back = createButton(this, px + pw - 52 - 170, by, { label: 'Back', width: 340, height: 60, align: 'center', accent, sound: 'ui_back', onClick: () => this.close() }, this.nav);
    this.helpFor.set(back, 'Return to the previous screen.');

    this.help = txt(this, cx, by - 64, '', { size: TYPE.small, color: COLORS.uiDim, originX: 0.5, align: 'center', wrap: pw - 140 });

    footerHints(this, [
      { action: 'navigateV', label: 'Select' },
      { action: 'navigateH', label: 'Adjust' },
      { action: 'back', label: 'Back' },
    ]);

    this.nav.focus(first, { silent: true });
    this.help.setText(this.helpFor.get(first) ?? '');
    this.nav.lock(180);

    staggerIn(this, [panel, title, sub], { dy: 14, duration: 260, stagger: 0 });
    staggerIn(this, [...rows, reset, back], { dx: -18, dy: 0, stagger: 22, duration: 260, delay: 60 });
    this.cameras.main.fadeIn(160, 5, 7, 11);
  }

  update(): void {
    this.nav?.update();
  }

  private confirmReset(): void {
    confirmDialog(this, {
      title: 'Reset all progress?',
      body: 'Cores, unlocked mechs, weapons, cosmetics, challenges and records will be wiped permanently. Your settings are kept.',
      confirmLabel: 'Wipe progress',
      cancelLabel: 'Keep progress',
      danger: true,
      parentNav: this.nav,
      onConfirm: () => {
        save.reset();
        sfx('ui_confirm');
        this.help.setText('Progress has been reset.');
        this.help.setColor('#ff5d73');
      },
    });
  }

  private close(): void {
    if (this.closing) return;
    this.closing = true;
    sfx('ui_back');
    const key = this.returnTo;
    const mgr = this.scene;
    if (mgr.isSleeping(key)) mgr.wake(key);
    else if (mgr.isPaused(key)) mgr.resume(key);
    else if (!mgr.isActive(key)) {
      mgr.start(key);
      return;
    }
    mgr.stop();
  }
}
